import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { UserService } from '../auth/_services/user.service';
import { map, switchMap } from 'rxjs/operators';
import { FacilityService } from '../configuration/_services/facility.service';
import { SystemService } from '../configuration/_services/system.service';
import { UnitService } from '../configuration/_services/unit.service';
import { CustomerService } from '../configuration/_services/customer.service';


const API_S3_SIGN_URL_UPLOAD = '/api/aws/uploadFile';
const API_GET_UPLOADED_FILE = '/api/aws/getUploadedFiles';
const API_CREATE_FOLDER = '/api/aws/createFolder';
const API_INIT_DATA = '/api/aws/initFolders';
const API_DELETE_FILE = '/api/aws/deleteFile';
const API_METADATA_FILE = '/api/aws/getMetaData';
const API_RENAME_FILE = '/api/aws/renameFile'
const API_AWS_FOLDER_STRUCTURE = '/api/aws/getFolderStructure';
const API_AWS_EXTRACT_ZIP = '/api/aws/extractZip';

const API_AZURE_SIGN_URL_UPLOAD = '/api/azure/uploadFile';
const API_AZURE_GET_UPLOADED_FILE = '/api/azure/getUploadedFiles';
const API_AZURE_CREATE_FOLDER = '/api/azure/createFolder';
const API_AZURE_INIT_DATA = '/api/azure/initFolders';
const API_AZURE_DELETE_FILE = '/api/azure/deleteFile';
const API_AZURE_METADATA_FILE = '/api/azure/getMetaData';
const API_AZURE_RENAME_FILE = '/api/azure/renameFile'
const API_AZURE_FOLDER_STRUCTURE = '/api/azure/getFolderStructure';
const API_AZURE_EXTRACT_ZIP = '/api/azure/extractZip';
const API_FILE_ACTIVITY = '/api/fileActivity';

const API_UPTIME_UPLOAD = '/api/uptime/uploadFile';
const API_UPTIME_GET_UPLOADED_FILE = '/api/uptime/getUploadedFiles';
const API_UPTIME_CREATE_FOLDER = '/api/uptime/createFolder';
const API_UPTIME_INIT_DATA = '/api/uptime/initFolders';
const API_UPTIME_DELETE_FILE = '/api/uptime/deleteFile';
const API_UPTIME_METADATA_FILE = '/api/uptime/getMetaData';
const API_UPTIME_RENAME_FILE = '/api/uptime/renameFile'
const API_URL = environment.API_URL;
const API_UPLOAD_FILE = '/api/uploadFile';
const API_GET_FOLDER_STRUCTURE = '/api/getFolderStructure';
@Injectable({
  providedIn: 'root'
})
export class FileBrowserService {

  flatTransformedData: any[] = []
  prevSelectedFileFolder: any = null;
  currentSelectedFolderPathInfo: { path: string, index: number } = {
    path: '',
    index: null
  };
  isFileUploading: boolean = false;
  fileUploadProgress: number = 0;
  folderName: string = "";
  duplicateFolderNamePresent: boolean = false;
  systemMap = new Map<string, string>();
  unitMap = new Map<string, string>();
  facilityMap = new Map<string, string>();
  customerMap = new Map<string, string>();
  onAzureDeployment : boolean;
  onVmDeployment  : boolean;

  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly facilityService: FacilityService,
    private readonly systemService: SystemService,
    private readonly unitService: UnitService,
    private readonly customerService: CustomerService
  ) {
    this.onAzureDeployment = environment.runtime.CLOUD_SERVICE_PROVIDER == 'azure';
    this.onVmDeployment = environment.runtime.CLOUD_SERVICE_PROVIDER == "vm";
  }

  uploadFile(file: File, fileName: string, folderDestination: string, type = 1): Observable<any> {
    return this.uploadFileFromFileBrowser(file, fileName, folderDestination, type);
  }

  uploadFileFromFileBrowser(file: File, fileName: string, folderDestination: string, type: number = 1): Observable<any> {
    if (this.onVmDeployment) {
      let queryParam = { fileDestination: folderDestination, fileName: fileName }
      const formData = new FormData();
      // Append the file
      formData.append('file', file);
      return this.http.post(API_URL + API_UPTIME_UPLOAD, formData, { headers: this.userService.getHttpAuthHeader(), params: queryParam, reportProgress: true, observe: 'events' })
    } else {
      const param: any = {
        fileName: fileName,
        fileType: file.type,
        fileDestination: folderDestination,
        userId: this.userService.getUserFromLocalStorage()._id,
        type: type
      };

      return this.http.get(API_URL + API_UPLOAD_FILE, { params: param, headers: this.userService.getHttpAuthHeader() })
        .pipe(
          map(data => data['data']),
          switchMap(data => {
            const signed_request = data['signed_request'];
            const fileUrl = data['url'];
            const putHeaders = new HttpHeaders(data['headers'] || {});
            const options = {
              headers: putHeaders,
              observe: 'events' as 'body',
              reportProgress: true
            };
            return this.http.put(signed_request, file, options).pipe(
              map(event => ({ ...event, fileUrl }))
            );
          })
        );
    }
  }

  getFilesAndFolders(folderName: string): Observable<any> {
    const queryParams = { folder: folderName ? folderName : '' };
    if (this.onVmDeployment) {
      return this.http.get(API_URL + API_UPTIME_GET_UPLOADED_FILE, { headers: this.userService.getHttpAuthHeader(), params: queryParams })
    }
    else {
      return this.http.get(API_URL + (this.onAzureDeployment ? API_AZURE_GET_UPLOADED_FILE : API_GET_UPLOADED_FILE), { headers: this.userService.getHttpAuthHeader(), params: queryParams });
    }
  }

  getSystemUnitFacilityList() {
    return forkJoin({
      systems: this.systemService.getSystem(0, {}, true, 'name'),
      units: this.unitService.getUnit('name', true),
      facilities: this.facilityService.getFacility(true, '', null, 'name'),
      customers: this.customerService.getCustomer(true, 'name')
    }).pipe(
      map(({ systems, units, facilities, customers }: any) => {
        this.systemMap = new Map(
          systems.data.map((s: any) => [s._id, s.name])
        );

        this.unitMap = new Map(
          units.data.map((u: any) => [u._id, u.name])
        );

        this.facilityMap = new Map(
          facilities.data.map((f: any) => [f._id, f.name])
        );

        this.customerMap = new Map(
          customers.data.map((c: any) => [c._id, c.name])
        );

        // return something if needed
        return true;
      })
    );
  }

  private getTargetMap(level: 'system' | 'unit' | 'facility' | 'customer') {
    switch (level) {
      case 'system':
        return this.systemMap;
      case 'unit':
        return this.unitMap;
      case 'facility':
        return this.facilityMap;
      case 'customer':
        return this.customerMap;
    }
  }


  updateSystemUnitFacilityList(level: 'system' | 'unit' | 'facility' | 'customer', action: 'upsert' | 'delete', data: { _id: string; name?: string }) {
    if (!data?._id) {
      return;
    }

    const targetMap = this.getTargetMap(level);

    if (!targetMap) {
      return;
    }

    if (action === 'delete') {
      targetMap.delete(data._id);
      return;
    }

    if (data.name) {
      targetMap.set(data._id, data.name);
    }
  }


  initFileBrowserData(): Observable<any> {
    this.flatTransformedData = [];
    let body = {};
    let systemsIds = [...this.userService.getUserFromLocalStorage().system];
    if (systemsIds && systemsIds.length > 0) {
      body['systems'] = systemsIds;
    }
    if (this.onVmDeployment) {
      return this.http.post(API_URL + API_UPTIME_INIT_DATA, body, { headers: this.userService.getHttpAuthHeader() })
    }
    else {
      return this.http.post(API_URL + (this.onAzureDeployment ? API_AZURE_INIT_DATA : API_INIT_DATA), body, { headers: this.userService.getHttpAuthHeader() });
    }
  }

  createFolder(folderName: string, folderDestination: string) {
    if (this.onVmDeployment) {
      let body = { folderDestination: folderDestination, folderName: folderName };
      return this.http.post(API_URL + API_UPTIME_CREATE_FOLDER, body, { headers: this.userService.getHttpAuthHeader() })
    }
    else {
      let queryParam = { folderDestination: folderDestination, folderName: folderName, userId: this.userService.getUserFromLocalStorage()._id };
      return this.http.get(API_URL + (this.onAzureDeployment ? API_AZURE_CREATE_FOLDER : API_CREATE_FOLDER), { headers: this.userService.getHttpAuthHeader(), params: queryParam })
    }
  }

  deleteFile(filePath: string) {
    let params = { filePath: filePath };
    if (this.onVmDeployment) {
      return this.http.delete(API_URL + API_UPTIME_DELETE_FILE, { headers: this.userService.getHttpAuthHeader(), body: params });
    }
    return this.http.get(API_URL + (this.onAzureDeployment ? API_AZURE_DELETE_FILE : API_DELETE_FILE), { headers: this.userService.getHttpAuthHeader(), params: params });
  }

  getMetaDataOfTheFile(filePath: string) {
    let queryParam = { filePath: filePath };
    if (this.onVmDeployment) {
      return this.http.get(API_URL + API_UPTIME_METADATA_FILE, { headers: this.userService.getHttpAuthHeader(), params: queryParam });
    }
    return this.http.get(API_URL + (this.onAzureDeployment ? API_AZURE_METADATA_FILE : API_METADATA_FILE), { headers: this.userService.getHttpAuthHeader(), params: queryParam });
  }

  renameFile(filePath: string, newFilePath: string) {
    let params = { currentPath: filePath, newPath: newFilePath };
    if (this.onVmDeployment) {
      return this.http.patch(API_URL + API_UPTIME_RENAME_FILE, params, { headers: this.userService.getHttpAuthHeader() });
    }
    return this.http.get(API_URL + (this.onAzureDeployment ? API_AZURE_RENAME_FILE : API_RENAME_FILE), { headers: this.userService.getHttpAuthHeader(), params: params });
  }

  extractZipFile(zipFilePath: string, destinationPath: string): Observable<any> {
    let params = { zipFilePath, currentPath: destinationPath };
    if (this.onVmDeployment) {
      return;
    } else {
      const endPoint = this.onAzureDeployment ? API_AZURE_EXTRACT_ZIP : API_AWS_EXTRACT_ZIP;
      return this.http.post(API_URL + endPoint, params, { headers: this.userService.getHttpAuthHeader() });
    }
  }

  getFolderStructure(): Observable<any> {
    if (this.onVmDeployment) {
      return;
    }
    let body = {};
    let systemsIds = this.userService.getUserFromLocalStorage().system;
    if (systemsIds && systemsIds.length > 0) {
      body['systems'] = systemsIds;
    }
    const endPoint = this.onAzureDeployment ? API_AZURE_FOLDER_STRUCTURE : API_AWS_FOLDER_STRUCTURE;
    return this.http.post(API_URL + endPoint, body, { headers: this.userService.getHttpAuthHeader() });
  }

  getFolderStructureForFileBrowser(): Observable<any> {
    if (this.onVmDeployment) {
      return;
    }
    let body = {};
    return this.http.post(API_URL + API_GET_FOLDER_STRUCTURE, body, { headers: this.userService.getHttpAuthHeader() });
  }

  transformObjectDataIntoFlatTree(data: any, index?: number) {
    for (let key of Object.keys(data)) {
      if (this.flatTransformedData.find(ele => ele['currentPath'] === key)) {
        delete data[key];
      }
    }
    let tail: any[] = [], level: number;
    if (this.flatTransformedData.length > 0) {
      tail = this.flatTransformedData.splice(index + 1);
      level = this.flatTransformedData[index].level;
    }
    Object.keys(data).forEach(key => {
      let currPathArr: any[] = [];
      if (key.charAt(key.length - 1) === '/') {
        currPathArr = key.slice(0, -1).split('/');
      }
      else currPathArr = key.split('/');
      const name = currPathArr[currPathArr.length - 1];
      if (this.isMongoId(name) && !this.getName(name)) {
        return;
      }
      let currentDataObj: any = { name: this.isMongoId(name) ? this.getName(name) : name, level: index === undefined ? 0 : level + 1, type: data[key].type, currentPath: key }
      if (data[key].type === "folder") {
        currentDataObj['isOpened'] = false;
      }
      else {
        if (this.onAzureDeployment && name === '.ignore') return;
        currentDataObj['url'] = data[key].url;
        currentDataObj['lastModified'] = data[key].lastModified;
      }
      const userObj = JSON.parse(localStorage.getItem('user'))
      // Apply permission filtering only for MongoID folders (entities like facility, unit, system)
      if (level === 2 && !userObj.admin && this.isMongoId(name)) {
        const permittedUnits = userObj.unit;
        if (permittedUnits.includes(name)) {
          this.flatTransformedData.push(currentDataObj);
        }
      }
      else if (level === 3 && !userObj.admin && this.isMongoId(name)) {
        const permittedSystems = userObj.system;
        if (permittedSystems.includes(name)) {
          this.flatTransformedData.push(currentDataObj);
        }
      }
      else
        this.flatTransformedData.push(currentDataObj);
    })
    this.flatTransformedData.push(...tail);
  }

  getCustomerName(id: string) {
    return this.customerMap.get(id);
  }

  getFacilityName(id: string) {
    return this.facilityMap.get(id);
  }

  getUnitName(id: string) {
    return this.unitMap.get(id);
  }

  getSystemName(id: string) {
    return this.systemMap.get(id);
  }

  getName(id: string) {
    return (
      this.facilityMap.get(id) ||
      this.unitMap.get(id) ||
      this.systemMap.get(id) ||
      this.customerMap.get(id)
    );
  }

  getFileNameAndExtension(fileName: string) {
    const splitIndex = fileName.lastIndexOf('.');
    const name = fileName.substring(0, splitIndex);
    const extension = fileName.substring(splitIndex + 1);
    return { name, extension };
  }

  goBackToPreviousSelectedFolder() {
    const folderPath = this.currentSelectedFolderPathInfo.path
    this.prevSelectedFileFolder = this.flatTransformedData.find(data => data.currentPath == folderPath)
    if (this.prevSelectedFileFolder) this.prevSelectedFileFolder.isSelected = true;
  }

  isMongoId(id: string) {
    const regex = /^[0-9a-fA-F]{24}$/;
    return regex.test(id);
  }

  openFileInNewTab(fileUrl: string): void {
    if (this.onVmDeployment) {
      fileUrl = `${location.protocol}//${fileUrl}`
    } else if (this.onAzureDeployment) {
      fileUrl = `${fileUrl}${localStorage.getItem('azureSas')}`;
    } else {
      fileUrl = `${fileUrl}${localStorage.getItem('awsAccessToken') ?? ''}`;
    }
    window.open(fileUrl, '_blank');
  }

  clearFileBrowserData() {
    this.flatTransformedData = [];
    this.prevSelectedFileFolder = null;
    this.currentSelectedFolderPathInfo = { path: '', index: null };
    this.isFileUploading = false;
    this.fileUploadProgress = 0;
    this.folderName = "";
    this.duplicateFolderNamePresent = false;
    this.systemMap.clear();
    this.unitMap.clear();
    this.facilityMap.clear();
    this.customerMap.clear();
  }

  logFileActivity(activityData: { fileURL: string, action: string, fileSize?: number, fileType?: string, status?: number, source?: number, session_id?: string }): Observable<any> {
    return this.http.post(API_URL + API_FILE_ACTIVITY, activityData, { headers: this.userService.getHttpAuthHeader() });
  }

  getFileActivities(params: any): Observable<any> {
    return this.http.get(API_URL + API_FILE_ACTIVITY, { params, headers: this.userService.getHttpAuthHeader() });
  }
}
